/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/* Import configuration */
import {
    BL_COST_LAST_14D_CRITICAL,
    BL_COST_LAST_14D_WARNING,
    BL_COST_LAST_CRITICAL,
    BL_COST_LAST_WARNING,
    BL_COST_OVERALL_CRITICAL,
    BL_COST_OVERALL_WARNING,
    BL_COST_YEARLY_CRITICAL,
    BL_COST_YEARLY_WARNING,
    BL_COST_MONTHLY_CRITICAL,
    BL_COST_MONTHLY_WARNING,
} from '@/config/app';

/* Import components */
import type { KpiCarouselApi2TimeSeriesItem } from '@/components/api/KpiCarouselApi2TimeSeries';

/* Import libraries */
import { roundToSignificantDigits } from '@/lib/formatting/significantDigits';
import { DeviceSensor } from "@/lib/sensors/catalog/deviceSensor";
import type { TimeSeriesTrafficLightConfig } from '@/lib/timeSeries/timeSeriesValue';

type Translate = (key: string, values?: Record<string, number | string>) => string;

interface BaseLoadDetailKpiConfigOptions {
    deviceSensor: DeviceSensor;
    t: Translate;
}

function createBaseLoadCostTrafficLight(
    warningThreshold: number,
    criticalThreshold: number,
): TimeSeriesTrafficLightConfig {
    return {
        rules: [
            { when: 'gte', value: criticalThreshold, severity: 'danger' },
            { when: 'gte', value: warningThreshold, severity: 'warning' },
        ],
        fallback: 'success',
    };
}

function createBaseLoadCostCarouselItem({
    key,
    title,
    urn,
    warningThreshold,
    criticalThreshold,
    lookback,
    noDataMessage,
}: {
    criticalThreshold: number;
    key: string;
    lookback?: KpiCarouselApi2TimeSeriesItem['lookback'];
    noDataMessage?: string;
    title: string;
    urn: string;
    warningThreshold: number;
}): KpiCarouselApi2TimeSeriesItem {
    return {
        aggregation: 'last',
        conversion: 1,
        fractionDigits: 2,
        key,
        lookback,
        noDataMessage,
        title,
        trafficLight: createBaseLoadCostTrafficLight(warningThreshold, criticalThreshold),
        trimFractionDigitsForIntegers: true,
        transformValue: (value: number) => roundToSignificantDigits(value, 2),
        unit: '€',
        urn,
    };
}

/**
 * Creates the base-load KPI definitions displayed above the prediction chart.
 */
export function createBaseLoadDetailKpiConfigs({
    deviceSensor,
    t,
}: BaseLoadDetailKpiConfigOptions) {
    const costOverallSensorName = deviceSensor.getSensorName('BL', 'CostOverall');
    const costDaySensorName = deviceSensor.getSensorName('BL', 'CostDay');

    if (costOverallSensorName === null || costDaySensorName === null) {
        return null;
    }

    return {
        /* KpiValueApi2TimeSeries: Current costs */
        currentCost: {
            aggregation: 'last' as const,
            conversion: 1,
            fractionDigits: 2,
            from: 'NOW-4WEEKS',
            showDate: true,
            showLegend: true,
            title: t('Current cost value'),
            to: 'NOW',
            trafficLight: createBaseLoadCostTrafficLight(BL_COST_LAST_WARNING, BL_COST_LAST_CRITICAL),
            trimFractionDigitsForIntegers: true,
            transformValue: (value: number) => roundToSignificantDigits(value, 2),
            unit: '€',
            urn: costDaySensorName,
        },

        /* KpiValueApi2TimeSeries: Costs last 14 days */
        costLast14Days: {
            aggregation: 'last-14' as const,
            conversion: 1,
            fractionDigits: 2,
            from: 'NOW-4WEEKS',
            showDate: true,
            showLegend: true,
            title: t('Cost last 14 days'),
            to: 'NOW',
            trafficLight: createBaseLoadCostTrafficLight(BL_COST_LAST_14D_WARNING, BL_COST_LAST_14D_CRITICAL),
            trimFractionDigitsForIntegers: true,
            transformValue: (value: number) => roundToSignificantDigits(value, 2),
            unit: '€',
            urn: costDaySensorName,
        },

        /* KpiCarouselApi2TimeSeries: Overall costs */
        costOverall: {
            autoAdvanceMs: 8000,
            from: 'NOW-25MONTHS',
            showDate: true,
            showLegend: true,
            to: 'NOW',
            items: [
                createBaseLoadCostCarouselItem({
                    criticalThreshold: BL_COST_OVERALL_CRITICAL,
                    key: 'cost_overall',
                    title: t('Cost overall'),
                    urn: costOverallSensorName,
                    warningThreshold: BL_COST_OVERALL_WARNING,
                }),
                createBaseLoadCostCarouselItem({
                    criticalThreshold: BL_COST_YEARLY_CRITICAL,
                    key: 'cost_last_year',
                    lookback: { amount: 1, toleranceDays: 31, unit: 'year' },
                    noDataMessage: t('No historical comparison value is available.'),
                    title: t('Cost last year'),
                    urn: costOverallSensorName,
                    warningThreshold: BL_COST_YEARLY_WARNING,
                }),
                createBaseLoadCostCarouselItem({
                    criticalThreshold: BL_COST_MONTHLY_CRITICAL,
                    key: 'cost_last_month',
                    lookback: { amount: 1, toleranceDays: 7, unit: 'month' },
                    noDataMessage: t('No historical comparison value is available.'),
                    title: t('Cost last month'),
                    urn: costOverallSensorName,
                    warningThreshold: BL_COST_MONTHLY_WARNING,
                }),
            ],
        },
    };
}
